import { Link, createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WikiAnchorSection } from '@/components/wiki/WikiAnchorSection';
import { WikiToc } from '@/components/wiki/WikiToc';
import { GuideActionIcon } from '@/components/icons/GuideActionIcon';

export const Route = createFileRoute('/combat/')({
  component: CombatPage,
});

function CombatPage() {
  const toc = [
    { id: 'combat-overview', label: 'Overview' },
    { id: 'tempo-and-risk', label: 'Tempo & Risk' },
    { id: 'attack-profiles', label: 'Attack Profiles' },
    { id: 'status-effects', label: 'Status Effects' },
  ];

  const attacks = ['traditional_march', 'ambush', 'conquest', 'massacre', 'plunder', 'raze_attack', 'reconnaissance'];

  return (
    <div className="space-y-5">
      <WikiToc items={toc} />

      <WikiAnchorSection id="combat-overview" title="Combat Doctrine">
        <div className="grid gap-4 sm:grid-cols-[130px_1fr]">
          <img src="/images/nav/combat@3x.png" alt="Combat" className="w-full max-w-[130px] rounded-xl border border-[#d4af37]/25" />
          <div className="space-y-3">
            <p>
              Combat is a timing system. Successful wars come from coordinated targets, synchronized returns,
              and correct action sequencing rather than isolated high-value hits.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge>Chain windows</Badge>
              <Badge>Return sync</Badge>
              <Badge>Defiance management</Badge>
            </div>
          </div>
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="tempo-and-risk" title="Tempo, Range, and Risk">
        <ul className="list-disc space-y-1 pl-5">
          <li>Respect relative size/networth windows to avoid low-yield, high-exposure exchanges.</li>
          <li>Travel time is part of cost; bad timing can erase tactical gains.</li>
          <li>General allocation should match target priority, not habit.</li>
          <li>Horses, buffs, and stance state can shift outcomes more than raw troop totals.</li>
        </ul>
      </WikiAnchorSection>

      <WikiAnchorSection id="attack-profiles" title="Attack Profiles">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {attacks.map(actionId => (
            <Link key={actionId} to="/actions/$actionId" params={{ actionId }}>
              <Card className="p-3 transition-colors hover:border-[#d4af37]/40">
                <div className="mb-2 flex items-center gap-3">
                  <GuideActionIcon
                    code={actionId}
                    alt={actionId.replace(/_/g, ' ')}
                    width={56}
                    height={56}
                    className="h-14 w-14 overflow-hidden rounded-lg border border-[#d4af37]/25"
                  />
                  <h3 className="font-cinzel text-lg text-[#f0f0f0]">{actionId.replace(/_/g, ' ')}</h3>
                </div>
                <p className="text-sm text-[#b0b0b0]">Open the dedicated action page for usage factors and strategic notes.</p>
              </Card>
            </Link>
          ))}
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="status-effects" title="Plague and Scourge Pressure">
        <p>
          Debuff states can decide war momentum by constraining income, BE, magic uptime, or draft recovery.
          Plan cure coverage and hospital/nature support before these effects stack into wider collapse.
        </p>
        <p>
          Cross-link: <Link to="/mystics" className="text-[#e6c547] underline">Mystics</Link>,{' '}
          <Link to="/economics" className="text-[#e6c547] underline">Economics</Link>,{' '}
          <Link to="/wiki/politics" className="text-[#e6c547] underline">Politics Hub</Link>.
        </p>
      </WikiAnchorSection>
    </div>
  );
}
